import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Typography,
  IconButton,
  Chip,
  Grid,
  Skeleton,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  Article as WordIcon,
  TableChart as ExcelIcon,
  Description as DocumentIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  MoreVert as MoreIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { formatDocumentTitle } from '../../utils/documentUtils';

interface Document {
  id: string;
  title: string;
  fileName?: string;
  description?: string;
  summary?: string;
  category: string;
  tags: string[];
  uploadedBy: string;
  uploadedAt: string;
  fileType: string;
  size: number;
  version: number;
  thumbnail?: string | null;
  analysisHistory?: any[];
}

interface DocumentGridViewProps {
  documents: Document[];
  loading?: boolean;
  onDocumentClick?: (documentId: string) => void;
}

const getFileIcon = (fileType: string) => {
  switch (fileType) {
    case 'pdf':
      return <PdfIcon />;
    case 'docx':
    case 'doc':
      return <WordIcon />;
    case 'xlsx':
    case 'xls':
      return <ExcelIcon />;
    default:
      return <DocumentIcon />;
  }
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const DocumentGridView: React.FC<DocumentGridViewProps> = ({
  documents,
  loading = false,
  onDocumentClick,
}) => {
  const { t } = useTranslation();

  const renderSkeleton = () => (
    <>
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={i}>
          <Card>
            <Skeleton variant="rectangular" height={140} />
            <CardContent>
              <Skeleton variant="text" height={32} />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="60%" />
            </CardContent>
            <CardActions>
              <Skeleton variant="circular" width={24} height={24} />
              <Skeleton variant="circular" width={24} height={24} />
            </CardActions>
          </Card>
        </Grid>
      ))}
    </>
  );

  if (loading) {
    return (
      <Grid container spacing={3}>
        {renderSkeleton()}
      </Grid>
    );
  }

  if (documents.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <DocumentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No documents found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Try adjusting your filters or search query
        </Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      {documents.map((doc) => (
        <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={doc.id}>
          <Card
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              '&:hover': {
                boxShadow: 4,
              },
            }}
            onClick={() => onDocumentClick?.(doc.id)}
          >
            {doc.thumbnail ? (
              <CardMedia
                component="img"
                height="140"
                image={doc.thumbnail}
                alt={doc.title}
              />
            ) : (
              <Box
                sx={{
                  height: 140,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'primary.light',
                }}
              >
                <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main' }}>
                  {getFileIcon(doc.fileType)}
                </Avatar>
              </Box>
            )}
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h6" gutterBottom noWrap>
                {formatDocumentTitle(doc.title, doc.fileName)}
              </Typography>
              {(doc.summary || doc.description) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    mb: 1,
                  }}
                >
                  {doc.summary || doc.description}
                </Typography>
              )}
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                <Chip label={doc.category} size="small" color="primary" variant="outlined" />

                {/* 分析済みタグの表示 */}
                {doc.analysisHistory && doc.analysisHistory.length > 0 && (
                  <Chip
                    label={t('documents.deepAnalyzed')}
                    size="small"
                    color="secondary"
                    sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 'bold' }}
                  />
                )}

                {doc.tags.slice(0, 2).map((tag) => (
                  <Chip key={tag} label={tag} size="small" variant="outlined" />
                ))}
                {doc.tags.length > 2 && (
                  <Chip label={`+${doc.tags.length - 2}`} size="small" variant="outlined" />
                )}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  {doc.uploadedBy}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(doc.size)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {formatDistanceToNow(new Date(doc.uploadedAt), { addSuffix: true })} • v{doc.version}
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'space-between', px: 2 }} onClick={(e) => e.stopPropagation()}>
              <Box>
                <Tooltip title={t('common.download')}>
                  <IconButton size="small">
                    <DownloadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('common.share')}>
                  <IconButton size="small">
                    <ShareIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              <IconButton size="small">
                <MoreIcon fontSize="small" />
              </IconButton>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default DocumentGridView;
